import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, TrendingUp, DollarSign, Users, BarChart3,
  Calculator, ArrowUpRight, Repeat, Zap, Target
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminRevenueProjections() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState({ newPartners: 10, convRate: 5, avgRevenue: 99, months: 12 });

  const headers = useCallback(() => {
    const token = document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1];
    return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
  }, []);

  useEffect(() => {
    fetch(`${API}/affiliate/admin/revenue-projections`, { headers: headers() })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [headers]);

  const projected = () => {
    if (!data) return { monthlyCommission: 0, annualCommission: 0, monthlyGross: 0, annualGross: 0 };
    const rate = data.settings?.pro_rate || 25;
    const monthlyConversions = scenario.newPartners * (scenario.convRate / 100);
    const monthlyGross = monthlyConversions * scenario.avgRevenue;
    const monthlyCommission = monthlyGross * (rate / 100);
    return {
      monthlyConversions: Math.round(monthlyConversions * 10) / 10,
      monthlyGross: Math.round(monthlyGross * 100) / 100,
      monthlyCommission: Math.round(monthlyCommission * 100) / 100,
      annualGross: Math.round(monthlyGross * scenario.months * 100) / 100,
      annualCommission: Math.round(monthlyCommission * scenario.months * 100) / 100,
    };
  };

  if (loading) return <div className="p-8 space-y-6">{[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-gray-50 rounded-xl animate-pulse border border-gray-100" />)}</div>;
  if (!data) return <div className="p-8 text-gray-500">Unable to load revenue projections.</div>;

  const m = data.current_metrics;
  const eb = data.event_breakdown;
  const proj = projected();

  return (
    <div className="max-w-6xl mx-auto" data-testid="revenue-projections-page">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Admin</Button></Link>
        <div>
          <h1 className="text-xl font-bold text-[#0f1f33]">Revenue Projections</h1>
          <p className="text-sm text-gray-400">Forecast affiliate program revenue based on current performance.</p>
        </div>
      </div>

      {/* Current Performance KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Active Partners", value: m.total_partners, icon: Users, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Monthly Avg Commission", value: fmt(m.monthly_avg_commission), icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Conversions/Month", value: m.conversions_per_month, icon: TrendingUp, color: "text-violet-600", bg: "bg-violet-50" },
          { label: "Click→Conv Rate", value: `${m.conversion_rate}%`, icon: Target, color: "text-amber-600", bg: "bg-amber-50" },
          { label: "Monthly Avg Gross", value: fmt(m.monthly_avg_gross), icon: BarChart3, color: "text-green-600", bg: "bg-green-50" },
          { label: "Pending Liability", value: fmt(m.pending_liability), icon: Calculator, color: "text-red-600", bg: "bg-red-50" },
          { label: "New Partners (30d)", value: m.partners_added_30d, icon: ArrowUpRight, color: "text-indigo-600", bg: "bg-indigo-50" },
          { label: "New Partners (60d)", value: m.partners_added_60d, icon: ArrowUpRight, color: "text-pink-600", bg: "bg-pink-50" },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-7 h-7 rounded-lg ${k.bg} flex items-center justify-center`}><k.icon className={`w-3.5 h-3.5 ${k.color}`} /></div>
              <span className="text-xs text-gray-400 font-medium">{k.label}</span>
            </div>
            <p className="text-lg font-bold text-[#0f1f33]">{k.value}</p>
          </div>
        ))}
      </div>

      {/* Event Breakdown */}
      <Card className="border-gray-100 shadow-sm mb-8">
        <CardContent className="p-6">
          <h3 className="font-semibold text-[#0f1f33] mb-4">90-Day Event Breakdown</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { label: "First Purchases", count: eb.first_purchases.count, total: eb.first_purchases.total, icon: Zap, color: "text-emerald-600", bg: "bg-emerald-50" },
              { label: "Renewals", count: eb.renewals.count, total: eb.renewals.total, icon: Repeat, color: "text-blue-600", bg: "bg-blue-50" },
              { label: "Upgrades", count: eb.upgrades.count, total: eb.upgrades.total, icon: ArrowUpRight, color: "text-violet-600", bg: "bg-violet-50" },
            ].map((e, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg ${e.bg} flex items-center justify-center`}><e.icon className={`w-4 h-4 ${e.color}`} /></div>
                  <span className="text-sm font-medium text-[#0f1f33]">{e.label}</span>
                </div>
                <p className="text-2xl font-bold text-[#0f1f33]">{e.count}</p>
                <p className="text-sm text-gray-400">{fmt(e.total)} in commissions</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Scenario Builder */}
      <Card className="border-gray-100 shadow-sm border-2 border-blue-100">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-50">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Calculator className="w-4.5 h-4.5 text-blue-600" /></div>
            <div>
              <h3 className="font-semibold text-[#0f1f33]">Scenario Builder</h3>
              <p className="text-xs text-gray-400">Model future revenue based on growth assumptions.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">New Partners / Month</label>
              <input type="number" value={scenario.newPartners} onChange={e => setScenario(p => ({ ...p, newPartners: parseInt(e.target.value) || 0 }))}
                className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm" data-testid="scenario-partners" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Conversion Rate (%)</label>
              <input type="number" value={scenario.convRate} onChange={e => setScenario(p => ({ ...p, convRate: parseFloat(e.target.value) || 0 }))}
                className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm" data-testid="scenario-conv-rate" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Avg Revenue ($)</label>
              <input type="number" value={scenario.avgRevenue} onChange={e => setScenario(p => ({ ...p, avgRevenue: parseFloat(e.target.value) || 0 }))}
                className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm" data-testid="scenario-avg-revenue" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Projection (Months)</label>
              <select value={scenario.months} onChange={e => setScenario(p => ({ ...p, months: parseInt(e.target.value) }))}
                className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm">
                <option value={3}>3 Months</option>
                <option value={6}>6 Months</option>
                <option value={12}>12 Months</option>
                <option value={24}>24 Months</option>
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-4 gap-4 bg-[#f0f4ff] rounded-xl p-5">
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Monthly Conversions</p>
              <p className="text-2xl font-bold text-[#0f1f33]" data-testid="proj-monthly-conv">{proj.monthlyConversions}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Monthly Commission</p>
              <p className="text-2xl font-bold text-[#2563eb]" data-testid="proj-monthly-comm">{fmt(proj.monthlyCommission)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">{scenario.months}-Month Gross</p>
              <p className="text-2xl font-bold text-[#0f1f33]">{fmt(proj.annualGross)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">{scenario.months}-Month Commission</p>
              <p className="text-2xl font-bold text-emerald-600" data-testid="proj-annual-comm">{fmt(proj.annualCommission)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
